'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAudioLimits, uploadAudio, followTranscription, cancelTranscription,
  type AudioLimits, type TranscribeProgress,
} from '@/lib/api';
import Layer from './Layer';

/**
 * Turn a voice file into text in the box.
 *
 * ── The shape of the wait ────────────────────────────────────────────────────
 *
 * This dialog is mostly about one thing: a transcription takes long enough that
 * the interface has to say what is happening, and it happens in two stages that
 * fail differently. Uploading is the user's network and can be slow on a phone;
 * transcribing is our model and is roughly six seconds per minute of audio.
 * They get one bar between them — the upload is the first fifth of it, the
 * chunks are the rest — because two bars in sequence read as two waits.
 *
 * The transcript appears WHILE it runs, chunk by chunk. That is the difference
 * between a progress bar you trust and one you watch suspiciously: the words
 * arriving are the proof.
 *
 * ── Why the limits are stated up front ───────────────────────────────────────
 *
 * Read from the server (`/api/transcribe/limits`) rather than hard-coded, so a
 * change to the backend's ceiling cannot leave the dialog promising something
 * it no longer delivers. They are shown BEFORE a file is chosen, because the
 * moment to learn a ten-minute cap exists is before uploading a lecture.
 */

type Phase = 'idle' | 'uploading' | 'working' | 'done' | 'error';

function prettyBytes(n: number): string {
  return n >= 1024 * 1024 ? `${Math.round(n / 1024 / 1024)} MB` : `${Math.max(1, Math.round(n / 1024))} kB`;
}

function prettyDuration(seconds: number): string {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  return m ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
}

export default function VoiceDialog({
  onClose,
  onTranscript,
}: {
  onClose: () => void;
  /** Called once, with the finished transcript, when the user takes it. */
  onTranscript: (text: string) => void;
}) {
  const [limits, setLimits] = useState<AudioLimits | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [uploadPct, setUploadPct] = useState(0);
  const [progress, setProgress] = useState<TranscribeProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const jobId = useRef<string | null>(null);
  const stop = useRef<(() => void) | null>(null);
  const abort = useRef<AbortController | null>(null);
  /* Drag events fire on every child element, so a plain boolean flickers the
     highlight off the moment the pointer crosses the text inside the zone.
     Counting enter/leave pairs is the standard fix. */
  const dragDepth = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    fetchAudioLimits(controller.signal).then(setLimits).catch(() => setLimits(null));
    return () => controller.abort();
  }, []);

  // Escape closes, and the panel takes focus so a screen reader lands inside it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whatever is in flight when this unmounts must be told to stop: an abandoned
  // job would otherwise hold one of the two concurrency slots for the whole file.
  useEffect(() => () => {
    stop.current?.();
    abort.current?.abort();
    if (jobId.current && phase !== 'done') void cancelTranscription(jobId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    stop.current?.();
    abort.current?.abort();
    if (jobId.current && phase !== 'done') void cancelTranscription(jobId.current);
    onClose();
  }

  const pick = useCallback((chosen: File | null | undefined) => {
    if (!chosen) return;
    setError(null);
    if (limits && chosen.size > limits.maxBytes) {
      setError(`That file is ${prettyBytes(chosen.size)} — the limit is ${prettyBytes(limits.maxBytes)}.`);
      return;
    }
    setFile(chosen);
    setPhase('idle');
    setProgress(null);
  }, [limits]);

  async function parse() {
    if (!file) return;
    setError(null);
    setPhase('uploading');
    setUploadPct(0);
    abort.current = new AbortController();

    try {
      const { id } = await uploadAudio(file, setUploadPct, abort.current.signal);
      jobId.current = id;
      setPhase('working');
      stop.current = followTranscription(id, (event) => {
        setProgress(event);
        if (event.stage === 'done') setPhase('done');
        if (event.stage === 'error') {
          // A late failure still hands over whatever was transcribed, so the
          // error is shown WITH the partial text rather than instead of it.
          setError(event.error ?? 'That recording could not be transcribed.');
          setPhase(event.text ? 'done' : 'error');
        }
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setError((err as Error).message || 'The upload failed.');
      setPhase('error');
    }
  }

  /**
   * One bar for two stages: the upload is the first 20%, the chunks the rest.
   *
   * Weighted that way because the upload is usually the shorter of the two and
   * a bar that reaches 90% on the upload and then sits still is worse than no
   * bar at all.
   */
  const pct = phase === 'uploading'
    ? Math.round(uploadPct * 20)
    : phase === 'working' || phase === 'done'
      ? 20 + Math.round(((progress?.done ?? 0) / Math.max(progress?.total ?? 1, 1)) * 80)
      : 0;

  const busy = phase === 'uploading' || phase === 'working';
  const transcript = progress?.text ?? '';

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) close(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Transcribe a voice file"
    >
      <div
        ref={panel}
        tabIndex={-1}
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-ink-200
                   bg-white shadow-2xl outline-none dark:border-ink-700 dark:bg-ink-900"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-ink-200 px-5 py-4 dark:border-ink-800">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-200/60
                           text-accent-700 dark:bg-accent-700/35 dark:text-accent-300">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9"
                 strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M5 11a7 7 0 0014 0M12 18v3" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Voice file to text</h2>
            <p className="truncate text-[11px] text-ink-500 dark:text-ink-400">
              The words come back in the language they were spoken in — translating them is the next step.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="rounded-full p-2 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700
                       dark:hover:bg-ink-800 dark:hover:text-ink-100"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {limits && !limits.enabled ? (
            <p className="text-sm text-ink-600 dark:text-ink-300">
              Voice files are switched off at the moment.
            </p>
          ) : (
            <>
              {/* The drop zone doubles as the file button: the whole thing is a
                  label, so a click anywhere in it opens the picker and there is
                  no separate "or" to read. */}
              <label
                onDragEnter={(e) => { e.preventDefault(); dragDepth.current++; setDragging(true); }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => {
                  e.preventDefault();
                  dragDepth.current = Math.max(0, dragDepth.current - 1);
                  if (!dragDepth.current) setDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  dragDepth.current = 0;
                  setDragging(false);
                  if (!busy) pick(e.dataTransfer.files?.[0]);
                }}
                className={
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed ' +
                  'px-6 py-8 text-center transition ' +
                  (dragging
                    ? 'border-accent-500 bg-accent-100/70 dark:border-accent-400 dark:bg-accent-700/25'
                    : 'border-ink-200 hover:border-ink-300 hover:bg-ink-100/60 dark:border-ink-700 dark:hover:bg-ink-800/60') +
                  (busy ? ' pointer-events-none opacity-60' : '')
                }
              >
                <input
                  ref={input}
                  type="file"
                  accept="audio/*,video/*,.m4a,.opus,.oga,.amr"
                  className="sr-only"
                  onChange={(e) => pick(e.target.files?.[0])}
                />
                <svg viewBox="0 0 24 24" className="h-7 w-7 text-ink-400" fill="none" stroke="currentColor"
                     strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 16V4M8 8l4-4 4 4" />
                  <path d="M4 16v2a3 3 0 003 3h10a3 3 0 003-3v-2" />
                </svg>
                <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                  {file ? file.name : 'Drop a voice file here'}
                </span>
                <span className="text-[12px] text-ink-500 dark:text-ink-400">
                  {file
                    ? `${prettyBytes(file.size)}${progress?.seconds ? ` · ${prettyDuration(progress.seconds)}` : ''} — click to choose a different one`
                    : 'or click to choose one'}
                </span>
              </label>

              {/* What we can actually take. Before the first upload, not after
                  it fails. */}
              {limits && (
                <ul className="mt-4 grid gap-1.5 text-[12px] text-ink-500 sm:grid-cols-2 dark:text-ink-400">
                  <li>· Up to {Math.round(limits.maxSeconds / 60)} minutes per file</li>
                  <li>· Up to {prettyBytes(limits.maxBytes)}</li>
                  <li className="sm:col-span-2">· {limits.formats.slice(0, 8).join(', ')} — and the audio out of a video</li>
                  <li className="sm:col-span-2">
                    · {limits.minutesPerDay} minutes a day, free · the transcript fills the box, up to{' '}
                    {limits.maxChars.toLocaleString()} characters
                  </li>
                </ul>
              )}

              {(busy || phase === 'done') && (
                <div className="mt-5">
                  <div className="flex items-baseline justify-between text-[12px] text-ink-500 dark:text-ink-400">
                    <span>
                      {phase === 'uploading' && 'Sending the file…'}
                      {phase === 'working' && progress && progress.total > 1 &&
                        `Transcribing — part ${Math.min(progress.done + 1, progress.total)} of ${progress.total}`}
                      {phase === 'working' && (!progress || progress.total <= 1) && 'Transcribing…'}
                      {phase === 'done' && !error && 'Done'}
                      {phase === 'done' && error && 'Stopped early'}
                    </span>
                    <span className="tabular-nums">{pct}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-200 dark:bg-ink-700">
                    <div
                      className={
                        'h-full rounded-full transition-[width] duration-300 ' +
                        (error ? 'bg-amber-500' : 'bg-accent-600 dark:bg-accent-400')
                      }
                      style={{ width: `${Math.max(pct, 3)}%` }}
                    />
                  </div>

                  {/* The transcript as it lands. Reading it is how you know the
                      bar is telling the truth. */}
                  {transcript && (
                    <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-ink-100 p-3 text-[13px]
                                    leading-relaxed text-ink-800 dark:bg-ink-800 dark:text-ink-100">
                      {transcript}
                    </div>
                  )}

                  {/* Length in, length out. A transcript that is thin for the
                      amount of audio is the one failure the server cannot see
                      for itself, and this is the number that makes it visible
                      to the person who can hear the recording. */}
                  {phase === 'done' && !error && transcript && progress?.seconds ? (
                    <p className="mt-2 text-[12px] text-ink-500 dark:text-ink-400">
                      {prettyDuration(progress.seconds)} of audio ·{' '}
                      {transcript.length.toLocaleString()} characters
                    </p>
                  ) : null}

                  {progress?.truncated && (
                    <p className="mt-2 text-[12px] text-amber-600 dark:text-amber-400">
                      The recording was longer than the box holds — the transcript stops at{' '}
                      {limits?.maxChars.toLocaleString()} characters.
                    </p>
                  )}
                </div>
              )}

              {error && (
                <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-600
                              dark:bg-red-500/10 dark:text-red-400">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ink-200 px-5 py-3
                        dark:border-ink-800">
          <button
            type="button"
            onClick={close}
            className="rounded-full px-4 py-2 text-[13px] font-medium text-ink-600 transition
                       hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
          >
            {busy ? 'Cancel' : 'Close'}
          </button>
          {phase === 'done' && transcript ? (
            <button
              type="button"
              onClick={() => { onTranscript(transcript); onClose(); }}
              className="rounded-full bg-accent-600 px-4 py-2 text-[13px] font-medium text-white transition
                         hover:brightness-110 dark:bg-accent-400 dark:text-accent-700"
            >
              Put it in the box
            </button>
          ) : (
            <button
              type="button"
              onClick={parse}
              disabled={!file || busy}
              className="rounded-full bg-accent-600 px-4 py-2 text-[13px] font-medium text-white transition
                         enabled:hover:brightness-110 disabled:opacity-40 dark:bg-accent-400 dark:text-accent-700"
            >
              {busy ? 'Working…' : 'Parse'}
            </button>
          )}
        </div>
      </div>
    </div>
    </Layer>
  );
}
